import React from 'react';
import { Helmet } from 'react-helmet';

interface SEOProps {
  title: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  ogUrl?: string;
  canonical?: string;
  publishDate?: string;
  modifiedDate?: string;
  author?: string;
  categories?: string[];
  tags?: string[];
  isArticle?: boolean;
}

/**
 * SEO component for managing meta tags
 */
const SEO: React.FC<SEOProps> = ({
  title,
  description = 'アラヨットラベル - 旅行ブログ。世界中の旅行情報、観光スポット、グルメ情報を提供します。',
  keywords = '旅行, 観光, 海外旅行, 国内旅行, 旅行ブログ, アラヨットラベル',
  ogImage = '/images/icon_yoko.jpg',
  ogType = 'website',
  ogUrl,
  canonical,
  publishDate,
  modifiedDate,
  author = 'アラヨットラベル',
  categories = [],
  tags = [],
  isArticle = false,
}) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://arayotravel.com';
  const fullUrl = ogUrl ? ogUrl : canonical ? `${siteUrl}${canonical}` : siteUrl;
  
  // 記事タイトルをサイト名と区別して設定（SNSカードでタイトルだけを表示するため）
  const metaTitle = isArticle ? title : `${title} | アラヨットラベル`;
  const fullTitle = `${title} | アラヨットラベル`;
  
  // OG画像のURLを適切に構築（常に絶対URLになるようにする）
  const fullImageUrl = ogImage ? (
    ogImage.startsWith('http') ? ogImage : // 既にフルURLの場合
    ogImage.startsWith('/') ? `${siteUrl}${ogImage}` : // 相対パスの場合
    `${siteUrl}/${ogImage}` // ベースURLがない場合
  ) : `${siteUrl}/images/icon_yoko.jpg`; // デフォルト画像
  
  const finalOgType = isArticle ? 'article' : ogType;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Google Search Enhanced Tags */}
      <meta name="thumbnail" content={fullImageUrl} />
      <meta itemProp="image" content={fullImageUrl} />
      <meta itemProp="name" content={metaTitle} />
      <meta itemProp="description" content={description} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={finalOgType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:site_name" content="アラヨットラベル" />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:alt" content={`${title} - アラヨットラベル`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:locale" content="ja_JP" />
      
      {isArticle && publishDate && (
        <meta property="article:published_time" content={publishDate} />
      )}
      {isArticle && modifiedDate && (
        <meta property="article:modified_time" content={modifiedDate} />
      )}
      {isArticle && author && (
        <meta property="article:author" content={author} />
      )}
      {isArticle && categories.map((category, index) => (
        <meta key={`category-${index}`} property="article:section" content={category} />
      ))}
      {isArticle && tags.map((tag, index) => (
        <meta key={`tag-${index}`} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={`${title} - アラヨットラベル`} />
      <meta name="twitter:site" content="@arayotravel" />
      <meta name="twitter:creator" content="@arayotravel" />
      
      {/* Canonical Link */}
      {canonical && <link rel="canonical" href={`${siteUrl}${canonical}`} />}
      {fullImageUrl && <link rel="image_src" href={fullImageUrl} />}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(
          isArticle
            ? {
                '@context': 'https://schema.org',
                '@type': 'BlogPosting',
                headline: title,
                description: description,
                image: {
                  '@type': 'ImageObject',
                  url: fullImageUrl,
                  width: 1200,
                  height: 630,
                  alt: `${title} - アラヨットラベル`
                },
                primaryImageOfPage: { 
                  '@type': 'ImageObject', 
                  url: fullImageUrl,
                  width: 1200,
                  height: 630
                },
                url: fullUrl,
                datePublished: publishDate,
                dateModified: modifiedDate || publishDate,
                author: {
                  '@type': 'Person',
                  name: author,
                  url: siteUrl
                },
                publisher: {
                  '@type': 'Organization',
                  name: 'アラヨットラベル',
                  url: siteUrl,
                  logo: {
                    '@type': 'ImageObject',
                    url: `${siteUrl}/images/icon_yoko.jpg`,
                    width: 600,
                    height: 60
                  }
                },
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  '@id': fullUrl
                },
                keywords: tags.join(', '),
                articleSection: categories.join(', '),
                inLanguage: 'ja-JP',
                isAccessibleForFree: true
              }
            : {
                '@context': 'https://schema.org',
                '@type': finalOgType === 'article' ? 'Article' : 'WebSite',
                headline: title,
                description: description,
                image: {
                  '@type': 'ImageObject',
                  url: fullImageUrl,
                  width: 1200,
                  height: 630,
                  alt: `${title} - アラヨットラベル`
                },
                url: fullUrl,
                inLanguage: 'ja-JP'
              }
        )}
      </script>
    </Helmet>
  );
};

export default SEO; 